import Layout from "../../../components/custom/Layout";
import useSWR from "swr";
import useUnit from "../../../customHooks/useUnit";
import styled from "styled-components";
import { customFetcher, notify } from "../../../utils";
import DisplayCard from "./DisplayCard";
import ModalUpload from "./ModalUpload";
import useModal from "../../../customHooks/useModal";
import Icon from "../../../components/custom/Icon";
import { EOS_GREEN } from "../../../constants/theme";
import apiClient from "../../../apiClient";

export type UploadType = 'image' | 'video';

export interface MediaItem {
    url: string;
    type: 'image' | 'video';
    name: string;
    id: number;
}

export default function ManageMediaCarousel() {
    const unit = useUnit();
    const { data = [], mutate, isValidating } = useSWR<MediaItem[]>(
        `/display/media-internal/${unit}`,
        customFetcher,
        { fallbackData: [] }
    );

    const { modal, setModal } = useModal();

    const handleSelectUpload = () => {
        setModal(true);
    }

    const deleteFile = async (mediaItem: MediaItem) => {

        const parts = mediaItem?.url?.split('https://azure-display-media-bzggc6h4a5eeaahh.z01.azurefd.net/azure-display-media/');
        if (parts && parts.length === 2) {
            const file = parts[1];
            await apiClient.post(`/deleteFile`, {
                id: mediaItem.id,
                s_file_name: file,
                s_container: 'azure-display-media'
            });
            const updatedData = data.filter(item => item.id !== mediaItem.id);
            mutate(updatedData, false);
            notify('Deleted');
        } else {
            notify('Error deleting file: failed to parse filename from url');
        }
    }

    return (
        <Layout>
            {/* Page container ensures the content area can scroll while the action bar stays fixed */}
            <h1>{unit} Display Media</h1>
            <Page>
                <CardsScrollArea>
                    <FlexContainer>
                        {data.map((item, i) => (
                            <DisplayCard item={item} key={item.id ?? i} deleteFile={deleteFile} />
                        ))}
                    </FlexContainer>
                </CardsScrollArea>

                {/* Sticky Action Bar */}
                <ActionBar role="toolbar" aria-label="Quick actions">
                    <Icon
                        className={'fa-solid fa-photo-film'}
                        size={60}
                        hoverColor={EOS_GREEN}
                        hoverScale={1.1}
                        onClick={() => handleSelectUpload()}
                    />
                </ActionBar>
            </Page>

            <ModalUpload modal={modal} setModal={setModal} mutate={mutate} />
        </Layout>
    );
}

/** Layout helpers **/
const Page = styled.div`
  /* Make the page fill the viewport so our scroll area can size correctly */
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  /* Space for the fixed action bar so content isn't hidden behind it */
  padding-bottom: 76px; /* matches ActionBar height + gap */
`;

const CardsScrollArea = styled.div`
  /* This makes only the card area scrollable */
  flex: 1 1 auto;
  overflow: auto;

  /* Smooth scrolling & momentum on iOS */
  -webkit-overflow-scrolling: touch;
`;

const FlexContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px;
`;

/** Bottom sticky bar **/
const ActionBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;

  /* Keep it above page content/modals */
  z-index: 1000;

  /* A little elevation and padding */
  display: flex;
  gap: 100px;
  justify-content: center;
  align-items: center;
  padding: 12px;

  /* Respect iOS safe-area */
  padding-bottom: calc(12px + env(safe-area-inset-bottom));

  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  border-top: 1px solid rgba(0,0,0,0.06);
`;
